import type {
  ApiResponse,
  CDKeyBatch,
  CDKeyListData,
  DashboardData,
  Device,
  LoginResponse,
  Order,
  Project,
  ProjectDashboardData,
  User,
  Version,
  WechatUser,
  WechatUserDetail,
} from '@/types/api'
import { useAuthStore } from '@/stores/auth'

/* eslint-disable no-undef */
const API_BASE = '/api/v1/admin'

function getToken() {
  return localStorage.getItem('admin_token')
}

function isJsonContentType(contentType: string | null): boolean {
  return !!contentType && contentType.includes('application/json')
}

export function getTokenExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: number }
    return payload.exp ?? null
  }
  catch {
    return null
  }
}

export function isTokenExpired(token: string): boolean {
  const exp = getTokenExp(token)
  if (!exp)
    return true
  return exp * 1000 < Date.now()
}

function clearAuthAndRedirect() {
  useAuthStore.getState().logout()
  window.location.href = '/login'
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  }
  const token = getToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  if (options.body && typeof options.body === 'string' && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json'
  }

  const resp = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  })

  if (resp.status === 401) {
    clearAuthAndRedirect()
    throw new Error('登录已过期，请重新登录')
  }

  const contentType = resp.headers.get('content-type')
  if (!isJsonContentType(contentType)) {
    const text = await resp.text()
    throw new Error(resp.ok ? 'Unexpected non-JSON response' : `HTTP ${resp.status}: ${text.slice(0, 200)}`)
  }

  const body = (await resp.json()) as ApiResponse<T>
  if (!resp.ok || body.code !== 0) {
    throw new Error(body.message || `HTTP ${resp.status}`)
  }
  return body
}

function buildQuery(
  params: Record<string, string | number | string[] | undefined>,
) {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') {
      continue
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== '') {
          query.append(key, item)
        }
      }
    }
    else {
      query.set(key, String(value))
    }
  }
  return query.toString()
}

export const api = {
  login: (email: string, password: string) =>
    request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  me: () => request<User>('/auth/me'),

  dashboard: () => request<DashboardData>('/dashboard'),

  projectDashboard: (project: string) =>
    request<ProjectDashboardData>(`/projects/${project}/dashboard`),

  projects: () => request<{ list: Project[] }>('/projects'),

  createProject: (data: {
    code: string
    name: string
    model: Project['model']
    description?: string
    templateCode?: string
  }) =>
    request<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteProject: (code: string) =>
    request<{ code: string }>(`/projects/${code}`, { method: 'DELETE' }),

  templates: () => request<{ list: Project[] }>('/projects/templates'),

  cdkeys: (
    project: string,
    params: {
      page?: number
      pageSize?: number
      keyword?: string
      batchId?: string
      status?: string
    },
  ) =>
    request<CDKeyListData>(`/projects/${project}/cdkeys?${buildQuery(params)}`),

  exportCdkeys: async (
    project: string,
    keyword?: string,
    batchId?: string,
    selectedCdKeys?: string[],
    status?: string,
  ) => {
    const resp = await fetch(
      `${API_BASE}/projects/${project}/cdkeys/export?${buildQuery({ keyword, batchId, cdKeys: selectedCdKeys, status })}`,
      {
        headers: { Authorization: `Bearer ${getToken() || ''}` },
      },
    )
    if (resp.status === 401) {
      clearAuthAndRedirect()
      throw new Error('登录已过期，请重新登录')
    }
    return resp
  },

  updateCdKey: (
    project: string,
    cdKey: string,
    data: { remark?: string, type?: string, plan?: string },
  ) =>
    request<{ cdKey: string }>(`/projects/${project}/cdkeys/${cdKey}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  batchOperateCdKeys: (
    project: string,
    data: {
      cdKeys: string[]
      action: 'revoke' | 'update_remark' | 'delete'
      remark?: string
    },
  ) =>
    request<{ action: string, count: number }>(
      `/projects/${project}/cdkeys/batch`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    ),

  importCdKeys: (
    project: string,
    data: { cdKeys: string[], name: string, plan: string, type: string },
  ) =>
    request<{ batchId: string, count: number }>(
      `/projects/${project}/cdkeys/import`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    ),

  batches: (project: string) =>
    request<{ list: CDKeyBatch[] }>(`/projects/${project}/batches`),

  createBatch: (
    project: string,
    data: {
      projectCode: string
      name: string
      plan: string
      type: string
      count: number
      prefix?: string
      length?: number
    },
  ) =>
    request<{ batchId: string, count: number, prefix: string }>(
      `/projects/${project}/batches`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  devices: (project: string, cdKey: string) =>
    request<{ list: Device[] }>(`/projects/${project}/cdkeys/${cdKey}/devices`),

  unbindDevice: (project: string, cdKey: string, mac: string) =>
    request<{ cdKey: string, mac: string, unbindTime: number }>(
      `/projects/${project}/cdkeys/${cdKey}/devices/${mac}/unbind`,
      { method: 'POST' },
    ),

  versions: (project: string) =>
    request<{ list: Version[] }>(`/projects/${project}/versions`),

  createVersion: (
    project: string,
    data: { version: string, content: string, isPublished: boolean },
  ) =>
    request<{ id: string }>(`/projects/${project}/versions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateVersion: (
    project: string,
    id: string,
    data: { version: string, content: string, isPublished: boolean },
  ) =>
    request<{ id: string }>(`/projects/${project}/versions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteVersion: (project: string, id: string) =>
    request<{ id: string }>(`/projects/${project}/versions/${id}`, {
      method: 'DELETE',
    }),

  orders: (
    project: string,
    params: { page?: number, pageSize?: number, keyword?: string },
  ) =>
    request<{ list: Order[], total: number, page: number, pageSize: number }>(
      `/projects/${project}/orders?${buildQuery(params)}`,
    ),

  users: () => request<{ list: User[] }>('/users'),

  createUser: (data: {
    email: string
    password: string
    role: User['role']
    projects: string[]
  }) =>
    request<{ email: string }>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateUser: (
    id: string,
    data: { email?: string, role?: User['role'], projects?: string[] },
  ) =>
    request<{ id: string }>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteUser: (id: string) =>
    request<{ id: string }>(`/users/${id}`, { method: 'DELETE' }),

  resetPassword: (id: string, password: string) =>
    request<{ id: string }>(`/users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  wechatUsers: (params: {
    page?: number
    pageSize?: number
    keyword?: string
  }) =>
    request<{
      list: WechatUser[]
      total: number
      page: number
      pageSize: number
    }>(`/wechat/users?${buildQuery(params)}`),

  wechatUserDetail: (openid: string) =>
    request<WechatUserDetail>(`/wechat/users/${openid}`),
}
